import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { List, Map } from 'immutable';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import AutoCompleteContainer from '../AutoComplete';
import { getChanged, unwrap } from '../../utils/ChangeSpy';
import config from '../../config';
import Popups from '../../components/Popup';
import {
  saveInfo,
  handleUpdateCompany,
  findUsersByRole,
  closeValidationError,
  saveBIC,
} from '../../actions/company/buyer/roleCreating';
import { TYPE_STRATEGIC } from '../../config/constants/common';
import { PrettyErrorProvider } from '../../contexts/PrettyErrorContext';
import { useRedirectForceContext } from '../../contexts/RedirectForceContext';
import { usePopupContext } from '../../contexts/PopupContext';

const defaultInputErrors = Map();

const BuyerRolePopupContainer = ({
  children,
  popup,
  info,
  businessModels,
  tags,
  inputErrors,
  isTarget,
  user,
  handleUpdateCompany,
  findUsersByRole,
  saveInfo,
  saveBIC,
  closeValidationError,
}) => {
  const redirectForce = useRedirectForceContext();
  const { openPopup, onClosePopup, safeClosePopup: closePopup } = usePopupContext();
  const fromPositionPopup = useRef(popup.getIn(['props', 'fromPositionPopup']));

  const [formError, setFormError] = useState({
    industryCategory: '',
    businessModel: '',
  });

  useEffect(() => {
    if (fromPositionPopup.current) {
      onClosePopup(popup.getIn(['props', 'cb']));
    }

    handleUpdateCompany({ name: 'buyerHarvcoLeadName', value: user.get('module') || '' });
    handleUpdateCompany({ name: 'buyerHarvcoLeadId', value: user.get('moduleId') || 0 });

    if (isTarget) {
      onClosePopup(() => {
        handleUpdateCompany({ name: 'disabledType', value: false });
      });
      handleUpdateCompany({ name: 'disabledType', value: true });
      handleUpdateCompany({ name: 'buyerType', value: TYPE_STRATEGIC });
    }
  }, []);

  const clearFormError = useCallback(() => {
    setFormError({ businessModel: '', industryCategory: '' });
  }, []);

  const handleErrorClose = useCallback(
    (event, field) => {
      closeValidationError({ field });
    },
    [closeValidationError],
  );

  const getIndustryTagIds = useCallback(tagList => {
    const result = [];

    tagList.forEach(tag => {
      if (tag.get('checked')) result.push(tag.get('id'));
      tag.get('children').forEach(child => {
        if (child.get('checked')) result.push(child.get('id'));
      });
    });

    return result;
  }, []);

  const getBusinessModelIds = useCallback(models => {
    const result = [];

    models.forEach(t => {
      if (t.get('checked')) result.push(t.get('id'));
    });

    return result;
  }, []);

  const isValidIndustryCategory = useCallback((ids, businessModelIds) => {
    let result = true;

    if (!ids.length) {
      setFormError(prev => ({ ...prev, industryCategory: 'Industry Tags is required' }));
      result = false;
    }

    if (!businessModelIds.length) {
      setFormError(prev => ({ ...prev, businessModel: 'Business Model is required' }));
      result = false;
    }

    return result;
  }, []);

  const handleChange = useCallback(
    event => {
      const { name, value } = event.target;

      handleUpdateCompany({ name, value });
    },
    [handleUpdateCompany],
  );

  const handleSuggestChange = useCallback(({ name, value }) => handleUpdateCompany({ name, value }), [
    handleUpdateCompany,
  ]);

  const handleSave = useCallback(
    event => {
      event.preventDefault();

      const current = getChanged(info);
      const companyId = popup.get('props').get('companyId');
      const industryCategoryIds = getIndustryTagIds(tags);
      const businessModelIds = getBusinessModelIds(businessModels);
      const body = { ...current, id: companyId };

      if (isValidIndustryCategory(industryCategoryIds, businessModelIds)) {
        saveInfo({
          companyId,
          body,
          afterSuccess: () => {
            saveBIC({
              companyId,
              data: industryCategoryIds.concat(businessModelIds).map(id => ({
                industryCategoryId: id,
              })),
              afterSuccess: () => {
                redirectForce(window.location);
              },
            });
          },
        });
      }
    },
    [
      info,
      popup,
      tags,
      businessModels,
      getIndustryTagIds,
      getBusinessModelIds,
      isValidIndustryCategory,
      saveInfo,
      saveBIC,
      redirectForce,
    ],
  );

  const handleClose = useCallback(() => {
    closePopup();
  }, [closePopup]);

  const handleClick = useCallback(() => {
    openPopup('TagsManagementPopup', {
      addNew: true,
      clientView: true,
      cb: () =>
        openPopup('BuyerRolePopup', {
          fromPositionPopup: fromPositionPopup.current,
          cb: popup.getIn(['props', 'cb']),
          companyId: popup.get('props').get('companyId'),
        }),
    });
  }, [openPopup, popup]);

  const suggestHarvco = (
    <AutoCompleteContainer
      idName="buyerHarvcoLeadId"
      inputProps={{
        name: 'buyerHarvcoLeadName',
        className: 'form-control',
        label: 'Primary HarvCo Contact',
      }}
      name="buyerHarvcoLeadName"
      onChange={handleSuggestChange}
      onFind={options => {
        findUsersByRole({ ...options, roles: [config.DIRECTOR] });
      }}
      rootPath={['targetCompany', 'buyer', 'roleCreating']}
      suggestionsName="buyerHarvcoLeadSuggests"
      valueName="buyerHarvcoLeadName"
      suggestIfEmpty
    />
  );

  return (
    <PrettyErrorProvider inputErrors={inputErrors || defaultInputErrors} onErrorClose={handleErrorClose}>
      <>
        <Popups.BuyerRolePopup
          businessModels={businessModels}
          error={{
            businessModel: formError.businessModel,
            industryCategory: formError.industryCategory,
          }}
          info={info}
          onChange={handleChange}
          onClick={handleClick}
          onClose={handleClose}
          onErrorClose={clearFormError}
          onSave={handleSave}
          suggestHarvco={suggestHarvco}
          tags={tags}
        />
        {children}
      </>
    </PrettyErrorProvider>
  );
};

function mapStateToProps(state, props) {
  return {
    ...props,
    isTarget: state.targetCompany.target.get('isTarget'),
    businessModels: unwrap(state.businessModels.get('models', List())),
    info: state.targetCompany.buyer.roleCreating,
    inputErrors: state.targetCompany.buyer.roleCreating.getIn(['inputErrors']),
    tags: state.industryTags.get('tags'),
    user: state.auth.get('user', null),
  };
}

export { BuyerRolePopupContainer };

export default connect(
  mapStateToProps,
  {
    saveInfo,
    handleUpdateCompany,
    findUsersByRole,
    closeValidationError,
    saveBIC,
  },
  mergeProps,
  connectOptions,
)(memo(BuyerRolePopupContainer));
